// Color utility functions for calendar events based on relay number

const RELAY_COLORS = [
  '#1976d2', // Relay 1 - Blue
  '#388e3c', // Relay 2 - Green
  '#f57c00', // Relay 3 - Orange
  '#7b1fa2', // Relay 4 - Purple
  '#c2185b', // Relay 5 - Pink
  '#00796b', // Relay 6 - Teal
  '#5d4037', // Relay 7 - Brown
  '#455a64', // Relay 8 - Blue Grey
  '#e64a19', // Relay 9 - Deep Orange
  '#303f9f', // Relay 10 - Indigo
];

/**
 * Get the base color for a relay
 * @param {number} relayNumber - The relay number (1-based)
 * @returns {string} - The hex color code
 */
export const getRelayBaseColor = relayNumber => {
  const index = (relayNumber - 1) % RELAY_COLORS.length;
  return RELAY_COLORS[index];
};

/**
 * Lighten a hex color by a percentage
 * @param {string} color - Hex color (e.g., '#1976d2')
 * @param {number} percent - Percentage to lighten (0-1)
 * @returns {string} - Lightened hex color
 */
const lightenColor = (color, percent) => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  const newR = Math.min(255, Math.floor(r + (255 - r) * percent));
  const newG = Math.min(255, Math.floor(g + (255 - g) * percent));
  const newB = Math.min(255, Math.floor(b + (255 - b) * percent));

  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
};

/**
 * Get the event color based on relay number and timing
 * @param {number} relayNumber - The relay number (1-based)
 * @param {Date} eventStart - Event start date
 * @param {Date} eventEnd - Event end date
 * @returns {string} - The color for the event
 */
export const getEventColor = (relayNumber, eventStart, eventEnd) => {
  const baseColor = getRelayBaseColor(relayNumber);
  const now = new Date();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const eventDate = new Date(eventStart);
  eventDate.setHours(0, 0, 0, 0);

  // Currently active events - full saturated color
  if (now >= eventStart && now <= eventEnd) {
    return baseColor;
  }

  // Events on today (even if future time) - full saturated color
  if (eventDate.getTime() === today.getTime()) {
    return baseColor;
  }

  // Future day events - moderately lighter but still visible
  if (eventDate > today) {
    return lightenColor(baseColor, 0.25);
  }

  // Past day events - more muted to clearly indicate they're in the past
  return lightenColor(baseColor, 0.35);
};

/**
 * Darken a hex color by a percentage
 * @param {string} color - Hex color (e.g., '#1976d2')
 * @param {number} percent - Percentage to darken (0-1)
 * @returns {string} - Darkened hex color
 */
const darkenColor = (color, percent) => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  const newR = Math.floor(r * (1 - percent));
  const newG = Math.floor(g * (1 - percent));
  const newB = Math.floor(b * (1 - percent));

  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
};
